import { ArrowDown } from 'lucide-react'
import { ButtonLink } from '@/components/ui/Button'
import { ScrollCue } from '@/components/ui/ScrollCue'
import { Picture } from '@/components/ui/Picture'

/**
 * Hero — MOW pattern. Full-bleed candid photo on the right, big chunky
 * headline overlapping on the left. Generous height, optimistic civic tone.
 */
export function Hero() {
  return (
    <section
      id="hero"
      aria-labelledby="hero-title"
      className="relative isolate overflow-hidden bg-surface lg:min-h-[calc(100svh-17rem)]"
    >
      {/* Photo: a clean band above the copy on mobile; full-bleed on the
          right, fading into the surface, from lg up. */}
      <div className="relative aspect-[16/10] w-full sm:aspect-[2/1] lg:absolute lg:inset-y-0 lg:left-1/3 lg:right-0 lg:aspect-auto lg:w-auto">
        <Picture
          name="hero-thriving"
          priority
          sizes="(min-width: 1024px) 67vw, 100vw"
          alt="A joyful multigenerational group — from their twenties to their eighties — laughing and celebrating together outdoors in golden light."
          className="h-full w-full object-cover object-[78%_center]"
        />
      </div>
      {/* Wash so the headline stays legible over the photo (desktop only) */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden bg-gradient-to-r from-surface via-surface/85 via-30% to-transparent to-55% lg:block"
      />

      <div className="relative mx-auto grid max-w-[88rem] grid-cols-1 items-center gap-12 px-6 pb-14 pt-8 sm:px-10 sm:pt-10 lg:min-h-[calc(100svh-17rem)] lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-7 xl:col-span-6">
          <span className="eyebrow animate-fade-up">
            U.S. Chapter · Founded 2026
          </span>

          {/* Not animated: the headline should paint immediately. */}
          <h1
            id="hero-title"
            className="mt-6 max-w-[16ch] font-display text-display-2 font-extrabold text-ink-deep"
          >
            Join us to make living longer, living better.
          </h1>

          <p
            style={{ animationDelay: '120ms' }}
            className="measure mt-8 animate-fade-up text-lg font-semibold leading-relaxed text-ink-deep sm:text-xl"
          >
            GIA Longevity is building the world we&rsquo;ll need for a life
            well lived to 100+, where longer lives mean better ones. We&rsquo;re
            bringing together leaders across industries to co-create solutions
            and offer resources to close the gaps in health, work, money, care,
            and wellbeing that longer lifespans are creating. Explore what
            we&rsquo;re building.
          </p>

          <div
            style={{ animationDelay: '220ms' }}
            className="mt-10 flex animate-fade-up flex-wrap items-center gap-4"
          >
            <ButtonLink href="#contact" variant="primary" size="lg">
              Become a partner
            </ButtonLink>
            <ButtonLink href="#challenge" variant="ghost" size="lg">
              <ArrowDown className="h-4 w-4" aria-hidden />
              The case for longevity
            </ButtonLink>
          </div>
        </div>
      </div>

      {/* Scroll cue pinned to the bottom of the viewport (Website Review V2) */}
      <ScrollCue />
    </section>
  )
}
